// src/CheckoutFieldsManager.js
import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelect } from "@wordpress/data";
import HideBillingFields from "./hide-removed-fields";
import SaphaliDateTimePicker from "./field-datepicker";
import logDebug, { waitForField, reorderFields, debounce, clearValidationErrorsInField, selectedPaymentMethod, selectedShippingMethod } from "./extraFunction.js";

// const CheckoutFieldsManager = ({ config }) => { return null };
const CheckoutFieldsManager = ({ config }) => {
  if (typeof window.wc !== "object") {
    console.warn("typeof window.wc !== 'object'");
    return null;
  }

  const [hideFields, setHideFields] = useState(config.hideFields || {});
  const [field_for_datetime, setField_for_datetime] = useState([]);
  const checkout_fields = config.checkout_fields || { billing: {}, shipping: {} };

  // Создаём списки для сравнения полей
  const hide_diff = {
    shipping: Object.keys(checkout_fields.billing || {}).filter(
      (x) => !Object.keys(checkout_fields.shipping || {}).includes(x)
    ),
    billing: Object.keys(checkout_fields.shipping || {}).filter(
      (x) => !Object.keys(checkout_fields.billing || {}).includes(x)
    ),
  };

  const pushfieldFordatetimecurent = useRef([]);
  const originalFieldStates = useRef({});

  // useMemo вычисляет и запоминает значение debounce-функции,
  // чтобы оно не пересоздавалось при каждом рендере, если зависимости не изменились.
  // В отличие от useEffect, который используется для выполнения
  // побочных эффектов после рендера компонента, useMemo возвращает вычисленное значение.
  const updateDateField = useMemo(
    () =>
      debounce(() => {
        setField_for_datetime([...new Set(pushfieldFordatetimecurent.current)]);
      }, 200),
    [useShippingAsBilling /* эта зависимость гарантирует пересоздание debounce-функции при изменении useShippingAsBilling */]
  );

  // Заслушка для country полей и обработчики change
  
  ["billing", "shipping"].forEach((area) => {
    waitForField(`#${area}-country`, (element) => {
      if (!element) {
        logDebug(`Поле ${area}-country не найдено.`, 'warn');
        return;
      }
      logDebug("saphli-field-control2", element.classList.contains("saphli-field-control2"));
      if (!element.classList.contains("saphli-field-control2")) {
        element.classList.add("saphli-field-control2");
        element.addEventListener("change", (event) => {
          const f = () => {
            logDebug(`Обработчик для поля ${area}-country.`);
            const value = event.target.value;
            if (value && checkout_fields[area]) {
              if (checkout_fields[area]["address_1"]) {
                reorderFields(area, {
                  ...checkout_fields[area],
                  "address_2-toggle": { index: checkout_fields[area]["address_1"].index, type: "toggle" },
                });
              } else {
                reorderFields(area, checkout_fields[area]);
              }
            }
          };
          debounce(f, 100)();
        });
      }
    });
  });

  // Перестановка полей после монитора
  useEffect(() => {
    const debounceReorder = () => {
      ["billing", "shipping"].forEach((area) => {
        if (checkout_fields[area]) {
          if (checkout_fields[area]["address_1"]) {
            reorderFields(area, {
              ...checkout_fields[area],
              "address_2-toggle": { index: checkout_fields[area]["address_1"].index, type: "toggle", label: "" },
            });
          } else {
            reorderFields(area, checkout_fields[area]);
          }
        }
      });
    };
    const timer = setTimeout(debounceReorder, 500);
    return () => clearTimeout(timer);
  }, [checkout_fields]);

  // Сохранение оригинального состояния полей
  useEffect(() => {
    const newOriginalStates = {};
    const computedHideFields = {};
    ["billing", "shipping", "additional-information", "contact"].forEach((area) => {
      if (!checkout_fields[area]) {
        logDebug(`checkout_fields[${area}] is undefined`);
        return;
      }
      const current = [...(config.hideFields[area] || []), ...(hide_diff[area] || [])];
      newOriginalStates[area] = current;
      computedHideFields[area] = (hideFields[area] || []).concat(
        (hide_diff[area] || []).filter((item) => !(hideFields[area] || []).includes(item))
      );
    });
    originalFieldStates.current = newOriginalStates;
    
  // Обновляем hideFields через useEffect (не в теле рендера)

    
    
    if (JSON.stringify(computedHideFields) !== JSON.stringify(hideFields)) {
      setHideFields(computedHideFields);
    }
  }, [checkout_fields, config.hideFields, hide_diff]); // зависимости
  
  // Обработка waitForField для date-полей
  ["billing", "shipping", "additional-information", "contact"].forEach((area) => {
    if (!checkout_fields[area]) {
      logDebug(`checkout_fields[${area}] is undefined`);
      return;
    }
    Object.entries(checkout_fields[area]).forEach(([fieldKey, fieldObj]) => {
      if (fieldObj.type === "date") {
        const added = "";
        // Первый селектор
        waitForField(`input[id="${area}-${fieldKey.replace(/saphali-/, "saphali/")}"]`, (element) => {
          if (element) {
            const selector = `${element.nodeName.toLowerCase()}[id="${element.getAttribute("id")}"]`;
            pushfieldFordatetimecurent.current.push(selector);
            updateDateField();
          }
        });
        // Второй селектор (если требуется)
        waitForField(`#${area}${added}-${fieldKey}`, (element) => {
          if (element) {
            const selector = `${element.nodeName.toLowerCase()}[id="${element.getAttribute("id")}"]`;
            pushfieldFordatetimecurent.current.push(selector);
            updateDateField();
          }
        });
      }
    });
  });

  // Используем useSelect для получения актуальных значений
  const useShippingAsBilling = useSelect(
    (select) => select("wc/store/checkout").getUseShippingAsBilling(),
    []
  );
  const ShippingMethod = useSelect((select) => selectedShippingMethod(select), []);
  const PaymentMethod = useSelect((select) => selectedPaymentMethod(select), []);

  const [differentFields, setDifferentFields] = useState({});
  
  // Обработка "различных" полей при изменении методов доставки/оплаты
  useEffect(() => {
    logDebug("Выбранный метод доставки:", ShippingMethod, "\nВыбранный метод оплаты:", PaymentMethod);

    const newHideFields = JSON.parse(JSON.stringify(originalFieldStates.current));
    let differentFieldsIn = {};
    ["shipping", "billing", "additional-information", "contact"].forEach((area) => {
      if (!checkout_fields[area]) {
        logDebug(`checkout_fields[${area}] is undefined`);
        return;
      }
      newHideFields[area] = [...new Set(newHideFields[area] ? [...newHideFields[area]] : [])];
      let applyRemoveError = [];
      Object.keys(checkout_fields[area]).forEach((v) => {
        if (
          JSON.stringify(checkout_fields[area][v].sm) !== JSON.stringify(["0"]) &&
          !checkout_fields[area][v]?.sm?.includes(ShippingMethod)
        ) {
          if (!newHideFields[area].includes(v)) {
            newHideFields[area].push(v);
          }
          applyRemoveError.push(clearValidationErrorsInField(`#${area}-${v}`, area) || `${area}-${v}`.replaceAll('-', "_"));
        }
        if (
          JSON.stringify(checkout_fields[area][v]?.pm) !== JSON.stringify(["0"]) &&
          !checkout_fields[area][v]?.pm?.includes(PaymentMethod)
        ) {
          if (!newHideFields[area].includes(v)) {
            newHideFields[area].push(v);
          }
          applyRemoveError.push(clearValidationErrorsInField(`#${area}-${v}`, area) || `${area}-${v}`.replaceAll('-', "_"));
        }
      });
      if(Array.isArray(applyRemoveError) && applyRemoveError.length !== 0)
        wp.data.dispatch('wc/store/validation').clearValidationErrors(applyRemoveError);
      differentFieldsIn = {
        ...differentFieldsIn,
        [area]: [...(hideFields[area] || []), ...(differentFields[area] || [])].filter((item) => (newHideFields[area] || []).indexOf(item) < 0),
      };
    });
    if (JSON.stringify(newHideFields) !== JSON.stringify(hideFields)) {
      setHideFields(newHideFields);
    }
    if(JSON.stringify(differentFieldsIn) !== JSON.stringify(differentFields))
      setDifferentFields(differentFieldsIn);
  }, [ShippingMethod, PaymentMethod, checkout_fields]);
  
  // Callback изменения даты
  const handleDateChange = (selector, value, location) => {
    if (["billing", "shipping"].includes(location)) {
      const cartData = wp.data.select("wc/store/cart").getCartData() || {};
      const fieldKey = selector
        .replace("#", "")
        .replace(/^billing-saphali-/, "saphali/")
        .replace(/^shipping-saphali-/, "saphali/")
        .replace(/^billing-saphali\//, "saphali\\/")
        .replace(/^shipping-saphali\//, "saphali\\/")
        .replace(/^billing-/, "")
        .replace(/^shipping-/, "");
      const updatedAddress = {
        ...cartData[`${location}Address`],
        [fieldKey]: value,
      };
      wp.data.dispatch("wc/store/cart").setCartData({
        ...cartData,
        [`${location}Address`]: updatedAddress,
      });
      logDebug(`Updated cart field ${fieldKey} =>`, value);
    } else if (["additional-information", "contact"].includes(location)) {
      const currentFields = wp.data.select("wc/store/checkout").getAdditionalFields() || {};
      const fieldKey = selector
        .replace("#", "")
        .replace(/^additional-information-saphali-/, "saphali/")
        .replace(/^contact-saphali-/, "saphali/")
        .replace(/^additional-information-/, "")
        .replace(/^contact-/, "");
      const updatedFields = {
        ...currentFields,
        [fieldKey]: value,
      };
      if (typeof wp.data.dispatch("wc/store/checkout").setAdditionalFields === "function") {
        wp.data.dispatch("wc/store/checkout").setAdditionalFields(updatedFields);
        logDebug(`Updated checkout field ${fieldKey} =>`, value);
      } else {
        wp.data.dispatch("wc/store/checkout").__internalSetExtensionData(fieldKey, { value });
        logDebug(`Updated checkout field ${fieldKey} =>`, value);
      }
    }
  };

  
  useEffect(() => {
    // Запускаем проверку только при инициализации или когда меняются checkout_fields
    if (!checkout_fields) return;
    // Проверка обязательных полей
    
    const checkRequiredFields = (location, addressObj) => {
      let applyRemoveError = [];
      Object.keys(addressObj).forEach((element) => {
        if (addressObj[element]?.required) {
          if (
            typeof wp?.data?.dispatch("wc/store/checkout")?.setEditingBillingAddress === "function" &&
            wp.data.select("wc/store/cart").getCustomerData()[`${location}Address`][element] === ""
          ) {
            if (location === "billing") {
              wp.data.dispatch("wc/store/checkout").setEditingBillingAddress(true);
            } else {
              wp.data.dispatch("wc/store/checkout").setEditingShippingAddress(true);
            }
            logDebug(`Mandatory field ${element} is empty => open editing ${location} address`);
          }
        } else {
          applyRemoveError.push (clearValidationErrorsInField(`#${location}-${element}`, location, true) || `${location}-${element}`.replaceAll('-', "_"));
        }
      });
      if(Array.isArray(applyRemoveError) && applyRemoveError.length !== 0)
        wp.data.dispatch('wc/store/validation').clearValidationErrors(applyRemoveError);
    };
    Object.keys(checkout_fields).forEach((area) => {
      logDebug("Processing required fields for area:", area);
      checkRequiredFields(area, checkout_fields[area]);
    });
  }, [checkout_fields]);


  logDebug("field_for_datetime", field_for_datetime);
  logDebug("UseShippingAsBilling:", useShippingAsBilling);
  logDebug("differentFields:", differentFields);
  
  return (
    <div>
      <HideBillingFields areas={["billing", "shipping"]} hideFields={hideFields} differentFields={differentFields} />
      <SaphaliDateTimePicker
        fieldIds={field_for_datetime}
        onDateChange={handleDateChange}
        type_field={config.onlyDate ? "onlydate" : "datetime"}
      />
    </div>
  );
};

export default CheckoutFieldsManager;
